import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
    Breadcrumbs,
    Grid,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Stack,
    Tooltip,
    IconButton,
    Switch,
    Button,
} from '@mui/material';
import { Home as HomeIcon, Visibility, Add } from '@mui/icons-material';
import PageTitle from '../../../../common/PageTitle';
import LinkNavegacao from '../../../../common/Link';
import { WritingDownIcon, EditIcon, TrashIcon, DownloadIcon } from '../../../../common/Icons';
import { FiltersButton } from '../../../../common/FiltersButton';
import { LoadingTableRow } from '../../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../../common/EmptyTableRow';
import { CustomTablePagination } from '../../../../common/CustomTablePagination';
import { TooltipSelectDisabled } from '../../../../common/TooltipSelectDisabled';
import { TutorialFilter } from './components/TutorialFilter';
import { TutorialForm } from './components/TutorialForm';
import { useCommonItems } from '../../../../../contexts/CommonItensProvider';
import { hasPermission, checkPermissionsAndRedirect } from '../../../../../configs/functions';
import { getTutoriais, updateTutorial, deletaTutorial, downloadTutorial } from './configs/functions';
import ConfirmDialog from '../../../../common/ConfirmDialog';
import { useMutation} from '@tanstack/react-query';

const PERMISSIONS = {
 admin: ['admin', 'admin_tutoriais']
};
const isAtivo = (status) => status === 1;

const Row = ({ tutorial, actionsAllowed, handleOpenEditForm, onSuccess, handleOpenDeleteDialog }) => {
    const { exibirAlerta } = useCommonItems();

    const [active, setActive] = useState(isAtivo(tutorial.status));
    const [isUpdating, setIsUpdating] = useState(false);


    useEffect(() => {
        setActive(isAtivo(tutorial.status));
    }, [tutorial.status]);

    const arquivo = (id) => {
        downloadTutorial(id);
    }
    const canUpdate = hasPermission(PERMISSIONS.admin);
    const canDestroy = hasPermission(PERMISSIONS.admin)

    const handleToggleTutorialStatus = async (event) => {
        if (!canUpdate) {
            exibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error');
            return;
        }
        const novoAtivo = event.target.checked;

        const novoStatus =typeof tutorial.status === 'number' ? novoAtivo ? 1 : 0 : novoAtivo ? 'ativo' : 'inativo';

        setActive(novoAtivo);
        setIsUpdating(true);

        try {
            await updateTutorial(tutorial.id, {status:novoStatus});
            exibirAlerta(
                'Sucesso',
                novoAtivo ? 'Tutorial ativado com sucesso!' : 'Tutorial desativado com sucesso!',
                'success'
            );
            onSuccess?.();
        } catch (error) {
            setActive(!novoAtivo);
            exibirAlerta(
                'Erro',
                error?.response?.data?.error || 'Erro ao atualizar tutorial.',
                'error'
            );
        } finally {
            setIsUpdating(false);
        }
    };

     return (
            <TableRow>
                <TableCell sx={{ fontSize: '0.9rem' }}>
                    {tutorial?.nome}
                </TableCell>

                <TableCell>
                    <Typography noWrap sx={{ display: 'block', width: '20rem' }}>
                        {tutorial?.descricao ?? '-'}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Typography>
                        { tutorial?.tipoArquivo.charAt(0).toUpperCase() + tutorial?.tipoArquivo.slice(1)}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Typography>
                        { tutorial?.tipo.charAt(0).toUpperCase() + tutorial?.tipo.slice(1)}
                    </Typography>
                </TableCell>

                <TableCell>
                    <Switch
                        color="primary"
                        checked={active}
                        onChange={handleToggleTutorialStatus}
                        disabled={!canUpdate || isUpdating}
                    />
                </TableCell>

                <TableCell>
                    <Stack direction="row" justifyContent="flex-end">
                    <IconButton aria-label="download" onClick={() => arquivo(tutorial.id)} size="small">
                        {<DownloadIcon />}
                    </IconButton>
                    {canUpdate && (
                        <Tooltip title={actionsAllowed ? 'Editar Tutorial' : 'Visualizar Tutorial'}>
                            <IconButton aria-label="Editar" onClick={handleOpenEditForm} size="small">
                               <EditIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {canDestroy && (
                        <Tooltip title={actionsAllowed ? 'Excluir Tutorial' : 'Visualizar Tutorial'}>
                            <IconButton aria-label="Excluir" onClick={handleOpenDeleteDialog} size="small">
                                {actionsAllowed ? <TrashIcon /> : <Visibility color="primary" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    </Stack>
                </TableCell>
            </TableRow>
    );
};


export function ListOfTutorials() {
    const { exibirAlerta } = useCommonItems();

    const [page, setPage] = useState(0);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);
    const [tutoriais, setTutoriais] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isOpenFilters, setIsOpenFilters] = useState(false);

    const [isOpenEditForm, setIsOpenEditForm] = useState(false);
    const [selectedTutorial, setSelectedTutorial] = useState(null);
    const [actionsAllowed] = useState(true);

    const [searchParams] = useSearchParams();
    const nome = searchParams.get('nome');
    const activeFilters = nome ? 1 : 0;

    const handleOpenEditForm = (tutorial) => {
        setSelectedTutorial(tutorial);
        setIsOpenEditForm(true);
    };

    const handleOpenCreateForm = () => {
        setSelectedTutorial(null);
        setIsOpenEditForm(true);
    };
    const handleOpenDeleteDialog = (item) => {
        setSelectedTutorial(item);
        setOpenDeleteDialog(true);
    }

    const { mutateAsync: handleDelete } = useMutation({
    mutationFn: deletaTutorial,
    onSuccess: (response, id) => {
        const message = response.data.message ?? 'Tutorial inativado com sucesso';
        exibirAlerta('Sucesso', message, 'success');
        carregaTutoriais();
    },
        onError: (error) => {
            const message = error.response?.data?.message ?? 'Erro ao executar operação';
            exibirAlerta('Erro', message, 'error');
        },
        onSettled: () => {
            setSelectedTutorial(null);
            setOpenDeleteDialog(false);
        },
    });

    async function carregaTutoriais() {
        const timer = setTimeout(() => setIsLoading(true), 500);

        try {
            const res = await getTutoriais({
                page,
                perPage: rowsPerPage,
                ...(nome ? { nome } : {}),
            });
           
            setTutoriais(res?.data?.data ?? []);
            setNumTotalItems(res.data.numero_total ?? 0);
        } catch (error) {
            exibirAlerta('Erro', 'Erro ao carregar tutoriais', 'error');
            setTutoriais([]);
            setNumTotalItems(0);
        } finally {
            clearTimeout(timer);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        carregaTutoriais();
    }, [page, rowsPerPage, nome]);

    useEffect(() => {
        setPage(0);
    }, [nome]);

    useEffect(() => {
        checkPermissionsAndRedirect(PERMISSIONS.admin);
    }, []);

    return (
        <>
            <Helmet title="Tutoriais" defer={false} />

            <Grid container direction="row" justifyContent="flex-start" alignItems="end" spacing={3}>
                <Grid item xs={12} md={9}>
                    <Breadcrumbs>
                        <LinkNavegacao to="/">
                            <HomeIcon fontSize="small" />
                        </LinkNavegacao>

                        <Typography variant="span">Admin Sempher</Typography>

                        <LinkNavegacao to="/admin/operacional">Gestão Operacional</LinkNavegacao>

                        <Typography variant="span">Tutoriais</Typography>
                    </Breadcrumbs>

                    <PageTitle
                        icon={<WritingDownIcon fontSize="large" />}
                        title="Tutoriais"
                        description="Administre Tutoriais e seus detalhes"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Stack
                        direction={{ xs: 'column-reverse', md: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <FiltersButton
                            isOpenFilters={isOpenFilters}
                            setIsOpenFilters={setIsOpenFilters}
                            activeFilters={activeFilters}
                        />

                        {hasPermission(PERMISSIONS.admin) && (
                            <Grid item xs={12} md={3}>
                                <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                    <TooltipSelectDisabled text="Cadastrar Tutorial">
                                        <Button
                                            variant="contained"
                                            startIcon={<Add />}
                                            onClick={handleOpenCreateForm}
                                        >
                                            Cadastrar Tutorial
                                        </Button>
                                    </TooltipSelectDisabled>
                                </Typography>
                            </Grid>
                        )}
                    </Stack>
                </Grid>

                <TutorialFilter isOpenFilters={isOpenFilters} isLoadingList={isLoading} />
                            
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table aria-label="Tutoriais">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome do Tutorial</TableCell>
                                    <TableCell>Descrição</TableCell>
                                    <TableCell>Visibilidade</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                
                                {isLoading ? (
                                    <LoadingTableRow />
                                ) : tutoriais.length > 0 ? (
                                    tutoriais.map((tutorial) => (
                                        <Row
                                            key={tutorial.id}
                                            tutorial={tutorial}
                                            actionsAllowed={ tutorial.status ? actionsAllowed : !actionsAllowed}
                                            handleOpenEditForm={() => handleOpenEditForm(tutorial)}
                                             handleOpenDeleteDialog={() => handleOpenDeleteDialog(tutorial)}
                                            onSuccess={carregaTutoriais}
                                        />
                                    ))
                                ) : (
                                    <EmptyTableRow colSpan={6} />
                                )}
                            </TableBody>
                        </Table>

                        <CustomTablePagination
                            numTotalItems={numTotalItems}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            setPage={setPage}
                            setRowsPerPage={setRowsPerPage}
                        />
                    </TableContainer>
                </Grid>

                <TutorialForm
                    open={isOpenEditForm}
                    setOpen={setIsOpenEditForm}
                    tutorial={selectedTutorial}
                    actionsAllowed={actionsAllowed}
                    onSuccess={carregaTutoriais}
                />

                
                        <ConfirmDialog
                            title={`Excluir Tutorial ${selectedTutorial?.nome}`}
                            description="Tem certeza que deseja excluir este tutorial?"
                            state={openDeleteDialog}
                            goAction={() => handleDelete(selectedTutorial.id)}
                            handleClose={() => {
                                setOpenDeleteDialog(false);
                                setSelectedTutorial(null);
                            }}
                        />
            </Grid>
        </>
    );
}